<!-- CMS Overview — a go-live checklist for a contractor's account. Pulls
     status from whatever already tracks each thing (website content in
     appConfig, /domains, /domain-reservations, /email-setup-requests)
     rather than introducing new state — this page is a summary, not a
     source of truth for any of it. -->
<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { API_BASE_URL, company, website, getAuthToken } from '@/stores/appConfig'

const router = useRouter()

async function authHeaders() {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  }
}

// ── Status signals ───────────────────────────────────────────────────────
const loading = ref(true)
const domains = ref([])
const domainReservations = ref([])
const emailRequest = ref(null)

function parseWebsiteContent(rawContent) {
  if (!rawContent) return {}
  if (typeof rawContent === 'object') return rawContent
  try {
    return JSON.parse(rawContent)
  } catch {
    return {}
  }
}

const hasWebsite = computed(() => Boolean(parseWebsiteContent(website.content)?.html || website.template_id))
const hasContactInfo = computed(() => Boolean(company.company_email))
const activeDomain = computed(() => domains.value.find(d => d.status === 'active'))
const pendingReservation = computed(() => domainReservations.value.find(r => r.status === 'pending'))

async function loadStatus() {
  if (!company.company_id) { loading.value = false; return }
  loading.value = true
  try {
    const headers = await authHeaders()
    const [domainsRes, reservationsRes, emailRes] = await Promise.all([
      fetch(`${API_BASE_URL}/domains?company_id=${company.company_id}`, { headers }),
      fetch(`${API_BASE_URL}/domain-reservations?company_id=${company.company_id}`, { headers }),
      fetch(`${API_BASE_URL}/email-setup-requests?company_id=${company.company_id}`, { headers }),
    ])
    const [domainsData, reservationsData, emailData] = await Promise.all([
      domainsRes.json().catch(() => ({})),
      reservationsRes.json().catch(() => ({})),
      emailRes.json().catch(() => ({})),
    ])
    if (domainsRes.ok) domains.value = domainsData.domains ?? []
    if (reservationsRes.ok) domainReservations.value = reservationsData.reservations ?? []
    if (emailRes.ok) emailRequest.value = emailData.request ?? null
  } catch {
    // Non-critical — worst case a step shows "not started" when it's
    // actually further along; nothing here is a source of truth.
  } finally {
    loading.value = false
  }
}

watch(() => company.company_id, loadStatus, { immediate: true })

// ── Checklist ─────────────────────────────────────────────────────────────
const checklist = computed(() => [
  {
    key: 'website',
    required: true,
    title: 'Build & save your website',
    description: 'Generate a page with the AI builder, start from a template, or paste your own HTML — then save it.',
    done: hasWebsite.value,
    statusLabel: hasWebsite.value ? 'Live' : 'Not started',
    cta: hasWebsite.value ? 'Edit site' : 'Build your site',
    to: '/cms/builder3',
  },
  {
    key: 'contact',
    required: true,
    title: 'Add your business contact info',
    description: 'Your email on file is where lead notifications go, and it\'s used as your default domain registrant contact.',
    done: hasContactInfo.value,
    statusLabel: hasContactInfo.value ? 'Complete' : 'Not started',
    cta: hasContactInfo.value ? 'Edit info' : 'Add info',
    to: '/settings',
  },
  {
    key: 'domain',
    required: false,
    title: 'Get a custom domain',
    description: 'Replace your free quotastic.ca URL with your own domain — includes your $15 CAD/month plan (CRM, leads, builder, hosting).',
    done: Boolean(activeDomain.value),
    inProgress: !activeDomain.value && Boolean(pendingReservation.value),
    statusLabel: activeDomain.value
      ? activeDomain.value.domain
      : pendingReservation.value
        ? `${pendingReservation.value.domain} — reserved`
        : 'Not started',
    cta: activeDomain.value ? 'Manage domain' : 'Get a domain',
    to: '/cms/domain',
  },
  {
    key: 'email',
    required: false,
    title: 'Set up professional email',
    description: 'A real inbox at your own domain (you@yourcompany.com) via Google Workspace — we can set it up for you.',
    done: false,
    inProgress: Boolean(emailRequest.value && ['new', 'in_progress'].includes(emailRequest.value.status)),
    statusLabel: emailRequest.value && ['new', 'in_progress'].includes(emailRequest.value.status)
      ? 'Requested'
      : 'Not started',
    cta: emailRequest.value ? 'View request' : 'Set up email',
    to: '/cms/email',
  },
])

const requiredSteps = computed(() => checklist.value.filter(item => item.required))
const requiredDone = computed(() => requiredSteps.value.filter(item => item.done).length)
const isFullyLive = computed(() => requiredSteps.value.every(item => item.done))

function goTo(item) {
  router.push(item.to)
}
</script>

<template>
  <main class="overview-page">
    <section class="overview-shell">
      <header class="page-header">
        <div>
          <p class="eyebrow">CMS</p>
          <h1>Overview</h1>
        </div>
      </header>

      <div v-if="loading" class="hint">Loading…</div>

      <template v-else>
        <div class="progress-card">
          <div class="progress-summary">
            <strong>{{ isFullyLive ? "You're live!" : `${requiredDone} of ${requiredSteps.length} required steps done` }}</strong>
            <p class="hint">Optional steps make your site more complete but aren't required to go live.</p>
          </div>
        </div>

        <ul class="checklist">
          <li v-for="item in checklist" :key="item.key" class="checklist-item">
            <div class="checklist-status">
              <v-icon v-if="item.done" size="22" color="#1f7a63">mdi-check-circle</v-icon>
              <v-icon v-else-if="item.inProgress" size="22" color="#b4820b">mdi-clock-outline</v-icon>
              <v-icon v-else size="22" color="var(--border-subtle)">mdi-circle-outline</v-icon>
            </div>
            <div class="checklist-body">
              <div class="checklist-title-row">
                <strong>{{ item.title }}</strong>
                <em v-if="!item.required" class="badge-optional">Optional</em>
              </div>
              <p class="hint">{{ item.description }}</p>
              <span class="checklist-status-label" :class="{ done: item.done, progress: item.inProgress }">{{ item.statusLabel }}</span>
            </div>
            <button class="btn-secondary" type="button" @click="goTo(item)">
              {{ item.cta }}
              <v-icon size="16">mdi-arrow-right</v-icon>
            </button>
          </li>
        </ul>
      </template>
    </section>
  </main>
</template>

<style scoped>
/* Tokens mirrored from DomainView.vue/EmailView.vue. */
.overview-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.overview-shell {
  width: min(820px, 100%);
  margin: 0 auto;
}

.page-header {
  margin-bottom: 20px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.hint {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.5;
}

.progress-card {
  padding: 18px 20px;
  margin-bottom: 18px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 2px 8px rgba(23, 35, 31, 0.05);
}

.progress-summary strong {
  display: block;
  margin-bottom: 4px;
  font-size: 1.1rem;
}

.checklist {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
}

.checklist-status {
  padding-top: 2px;
}

.checklist-body {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 4px;
}

.checklist-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-optional {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--surface-alt);
  color: var(--text-secondary);
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  font-style: normal;
}

.checklist-status-label {
  margin-top: 4px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.checklist-status-label.done { color: #1f7a63; }
.checklist-status-label.progress { color: #b4820b; }

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 750;
  cursor: pointer;
  white-space: nowrap;
}
</style>
